import React from 'react';
import PropTypes from 'prop-types';
import { Send, MessageSquare, Check, X } from 'lucide-react';
import BlueGeckoIcon from '../../../../assets/chatbot.png';

const AIChat = ({
  scAiInput,
  setScAiInput,
  scAiMessages,
  scAiLoading,
  handleAiAlter,
  scPendingChange,
  handleAcceptChange,
  handleRejectChange
}) => {
  const scrollRef = React.useRef(null);

  React.useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [scAiMessages, scAiLoading]);

  const suggestions = [
    'Add indexes / constraints',
    'Optimise for performance',
    'Convert to upsert / soft-delete',
    'Explain any SQL block',
  ];

  return (
    <div className="scriptly-ai-sidebar">
      <div className="ai-header">
        <div className="ai-avatar shadow-[0_0_12px_rgba(59,130,246,0.2)]" style={{ background: 'transparent' }}>
          <img src={BlueGeckoIcon} alt="BlueGecko AI" className="w-full h-full object-contain" />
        </div>
        <div>
          <div className="text-[13px] font-bold text-slate-600">BlueGecko AI</div>
          <div className="text-[10px] text-slate-500 font-medium">Ask BlueGecko to modify or explain the script</div>
        </div>
      </div>

      <div className="ai-chat-messages" ref={scrollRef}>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
            <MessageSquare size={14} className="text-blue-500" />
          </div>
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Assistant</span>
        </div>

        {scAiMessages.length === 0 ? (
          <div className="ai-suggestion-box">
            <div className="text-[12px] font-bold text-blue-400 mb-3">Hi! I can help you:</div>
            <div className="flex flex-col gap-2.5">
              {suggestions.map((s) => (
                <button
                  key={s}
                  className="text-left text-[11px] text-slate-400 hover:text-white cursor-pointer bg-transparent border-none p-0 outline-none transition-colors"
                  onClick={() => handleAiAlter(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          scAiMessages.map((msg, i) => (
            <div key={`${msg.role}-${i}`} className={`chat-bubble ${msg.role === 'ai' ? 'ai' : 'user'} ${msg.hasPendingChange && scPendingChange ? 'has-pending' : ''} shadow-sm`}>
              <div className="font-medium mb-1">{msg.text}</div>
              {msg.explanation && Array.isArray(msg.explanation) && (
                <ul className="ai-explanation-list">
                  {msg.explanation.map((item, idx) => (
                    <li key={idx} className="ai-explanation-item">
                      {item}
                    </li>
                  ))}
                </ul>
              )}
              {msg.role === 'ai' && msg.hasPendingChange && scPendingChange && scPendingChange.messageId === msg.id && (
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-200">
                  <button
                    className="flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md text-[10px] font-bold transition-colors shadow-sm"
                    onClick={handleAcceptChange}
                  >
                    <Check size={12} />
                    Apply Changes
                  </button>
                  <button
                    className="flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-md text-[10px] font-bold transition-colors shadow-sm"
                    onClick={handleRejectChange}
                  >
                    <X size={12} />
                    Discard
                  </button>
                </div>
              )}
            </div>
          ))
        )}
        {scAiLoading && <div className="chat-bubble ai italic animate-pulse">Analyzing your script and applying improvements...</div>}
      </div>

      <div className="chat-input-area">
        <div className="chat-input-container shadow-inner">
          <textarea
            rows={2}
            value={scAiInput}
            onChange={(e) => setScAiInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleAiAlter(scAiInput);
              }
            }}
            placeholder="Describe transformation..."
            className="chat-textarea"
          />
          <Send
            size={14}
            className="chat-send-inline"
            onClick={() => handleAiAlter(scAiInput)}
          />
        </div>

      </div>
    </div>
  );
};

AIChat.propTypes = {
  scAiInput: PropTypes.string,
  setScAiInput: PropTypes.func,
  scAiMessages: PropTypes.arrayOf(PropTypes.shape({
    role: PropTypes.oneOf(['ai', 'user']),
    text: PropTypes.string,
    explanation: PropTypes.arrayOf(PropTypes.string)
  })),
  scAiLoading: PropTypes.bool,
  handleAiAlter: PropTypes.func,
  scPendingChange: PropTypes.object,
  handleAcceptChange: PropTypes.func,
  handleRejectChange: PropTypes.func
};

export default AIChat;
